import { AspectRatio } from '../types';
import {
  prefetchAllImageUrls,
  inlineImagesFromCache,
  inlineExternalImages,
  restoreOriginalImages,
} from '../lib/imageCompression';
import { getFontEmbedCSS, collectUsedFontFaces } from '../lib/fontEmbed';
import { hasBlankImageRegions } from '../lib/captureVerify';

// External libs come from CDN scripts in index.html.
declare global {
  interface Window {
    htmlToImage: {
      toBlob: (element: HTMLElement, options?: Record<string, unknown>) => Promise<Blob | null>;
      toPng: (element: HTMLElement, options?: Record<string, unknown>) => Promise<string>;
    };
    html2canvas: (
      element: HTMLElement,
      options?: Record<string, unknown>,
    ) => Promise<HTMLCanvasElement>;
    JSZip: new () => {
      file: (name: string, data: Blob) => void;
      generateAsync: (options: { type: 'blob' }) => Promise<Blob>;
    };
    saveAs: (blob: Blob, filename: string) => void;
  }
}

export const NATIVE_SLIDE_WIDTH = 1080;

export function nativeSlideHeight(aspectRatio: AspectRatio): number {
  const [w, h] = aspectRatio.split('/').map((n) => Number(n));
  if (!w || !h) return Math.round(NATIVE_SLIDE_WIDTH * 1.25);
  return Math.round((NATIVE_SLIDE_WIDTH * h) / w);
}

function isIOSWebKit(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/**
 * Resolves once every `<img>` inside `element` has either loaded+decoded or
 * errored. Bounded by a 10s safety timeout so a broken image doesn't stall
 * the export forever.
 */
function waitForImages(element: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    let resolved = false;
    const done = () => {
      if (!resolved) {
        resolved = true;
        resolve();
      }
    };
    const images = element.querySelectorAll('img');
    if (images.length === 0) {
      done();
      return;
    }
    let loaded = 0;
    const checkDone = () => {
      loaded++;
      if (loaded >= images.length) done();
    };
    images.forEach((img) => {
      if (img.complete && img.naturalHeight > 0) {
        img.decode().then(checkDone).catch(checkDone);
      } else {
        img.onload = () => img.decode().then(checkDone).catch(checkDone);
        img.onerror = checkDone;
      }
    });
    window.setTimeout(done, 10_000);
  });
}

/**
 * html2canvas ignores CSS object-fit. Pre-crop every <img> with
 * object-fit:cover into a temporary canvas and replace its src with the
 * cropped data URI. Call `restoreObjectFitImages` after capture to revert.
 */
function preProcessObjectFitImages(
  root: HTMLElement,
): Map<HTMLImageElement, { src: string; objectFit: string; objectPosition: string }> {
  const originals = new Map<
    HTMLImageElement,
    { src: string; objectFit: string; objectPosition: string }
  >();
  root.querySelectorAll('img').forEach((img) => {
    const computed = window.getComputedStyle(img);
    if (computed.objectFit !== 'cover') return;
    if (!img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) return;
    const cw = img.clientWidth;
    const ch = img.clientHeight;
    if (cw === 0 || ch === 0) return;

    const imgRatio = img.naturalWidth / img.naturalHeight;
    const containerRatio = cw / ch;
    let sx: number, sy: number, sw: number, sh: number;
    if (imgRatio > containerRatio) {
      sh = img.naturalHeight;
      sw = sh * containerRatio;
      sx = (img.naturalWidth - sw) / 2;
      sy = 0;
    } else {
      sw = img.naturalWidth;
      sh = sw / containerRatio;
      sx = 0;
      sy = (img.naturalHeight - sh) / 2;
    }

    const objPos = computed.objectPosition || '50% 50%';
    const parts = objPos.split(/\s+/);
    const vPart = parts.length >= 2 ? parts[1] : '50%';
    if (vPart.endsWith('%')) {
      const pct = parseFloat(vPart) / 100;
      if (imgRatio <= containerRatio) {
        const maxSy = img.naturalHeight - sh;
        sy = maxSy * pct;
      }
    }
    const hPart = parts[0] || '50%';
    if (hPart.endsWith('%')) {
      const pct = parseFloat(hPart) / 100;
      if (imgRatio > containerRatio) {
        const maxSx = img.naturalWidth - sw;
        sx = maxSx * pct;
      }
    }

    const tmp = document.createElement('canvas');
    tmp.width = cw;
    tmp.height = ch;
    const ctx = tmp.getContext('2d');
    if (!ctx) return;
    try {
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
    } catch {
      return;
    }
    originals.set(img, {
      src: img.src,
      objectFit: img.style.objectFit || '',
      objectPosition: img.style.objectPosition || '',
    });
    img.src = tmp.toDataURL('image/png');
    img.style.objectFit = 'fill';
    img.style.objectPosition = 'center center';
    tmp.width = 0;
    tmp.height = 0;
  });
  return originals;
}

function restoreObjectFitImages(
  originals: Map<HTMLImageElement, { src: string; objectFit: string; objectPosition: string }>,
) {
  originals.forEach(({ src, objectFit, objectPosition }, img) => {
    img.src = src;
    img.style.objectFit = objectFit;
    img.style.objectPosition = objectPosition;
  });
}

/**
 * html2canvas on iOS WebKit interprets unitless CSS line-height values
 * differently from the live renderer. Convert every unitless line-height /
 * font-size to explicit px so the captured PNG matches the preview.
 */
function preProcessTextForIOSCapture(
  root: HTMLElement,
): Map<HTMLElement, { lineHeight: string; fontSize: string }> {
  const originals = new Map<HTMLElement, { lineHeight: string; fontSize: string }>();
  const textElements = root.querySelectorAll<HTMLElement>(
    'h1, h2, h3, p, span, div, strong, em, s, u, a',
  );
  textElements.forEach((el) => {
    const computed = window.getComputedStyle(el);
    if (
      el.tagName === 'DIV' &&
      (computed.display === 'flex' ||
        computed.display === 'inline-flex' ||
        computed.display === 'grid' ||
        computed.display === 'inline-grid')
    ) {
      return;
    }
    const fontSize = parseFloat(computed.fontSize);
    const lineHeight = computed.lineHeight;
    originals.set(el, {
      lineHeight: el.style.lineHeight || '',
      fontSize: el.style.fontSize || '',
    });
    if (lineHeight === 'normal') {
      el.style.lineHeight = `${Math.floor(fontSize * 1.2)}px`;
    } else {
      const lhPx = parseFloat(lineHeight);
      if (!isNaN(lhPx) && lhPx > 0) {
        el.style.lineHeight = `${Math.floor(lhPx)}px`;
      }
    }
    if (!isNaN(fontSize) && fontSize > 0) {
      el.style.fontSize = `${fontSize}px`;
    }
  });
  return originals;
}

function restoreTextAfterIOSCapture(
  originals: Map<HTMLElement, { lineHeight: string; fontSize: string }>,
) {
  originals.forEach(({ lineHeight, fontSize }, el) => {
    el.style.lineHeight = lineHeight;
    el.style.fontSize = fontSize;
  });
}

/**
 * Captures one element as a PNG Blob.
 *  - iOS WebKit path uses html2canvas (Canvas 2D, no SVG foreignObject) and
 *    applies object-fit + text preprocessing that html2canvas needs.
 *  - Desktop / Android falls back to html-to-image's SVG approach.
 * In both paths, external image URLs are inlined (from `imageCache`) so a
 * tainted canvas never silently yields a blank PNG.
 */
async function captureElementAsBlob(
  element: HTMLElement,
  opts: { width: number; height: number; bgColor: string; imageCache: Map<string, string>; fontEmbedCSS?: string },
): Promise<Blob | null> {
  const { width, height, bgColor, imageCache, fontEmbedCSS } = opts;

  // Inline from the pre-fetched cache first, then fetch whatever is still
  // remote (cache misses from a failed prefetch). inlineExternalImages skips
  // data: URIs, so it's a no-op when the cache covered everything.
  const fromCache = imageCache.size > 0
    ? inlineImagesFromCache(element, imageCache)
    : new Map<HTMLImageElement, string>();
  const fetched = await inlineExternalImages(element);
  const inlined = new Map([...fromCache, ...fetched]);

  await waitForImages(element);

  try {
    // PRIMARY (all platforms, including iOS): html-to-image. The SVG
    // foreignObject is rendered by the browser's own layout engine, so
    // exported text (handle, name, badge, page numbers) matches the on-screen
    // preview exactly. html2canvas re-implements CSS layout and drifts text
    // baselines a few px downward on iOS WebKit, so it is only a fallback.
    // WebKit rasterizes the SVG foreignObject before large inline images
    // finish decoding, yielding blank rectangles on the first capture of a
    // session ("white images" on Safari). Verify the output and re-capture:
    // by the retry the decode cache is warm and the images paint correctly.
    // On browsers without the race the first pass verifies clean.
    let blob: Blob | null = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      blob = await window.htmlToImage.toBlob(element, {
        width,
        height,
        backgroundColor: bgColor,
        pixelRatio: 1,
        cacheBust: true,
        // Embed the actual web fonts so serif/display fonts don't fall back to
        // system fonts (which overflow). Falls back to skipFonts if embedding failed.
        ...(fontEmbedCSS ? { fontEmbedCSS } : { skipFonts: true }),
      });
      if (blob && !(await hasBlankImageRegions(blob, element))) return blob;
    }

    // FALLBACK (iOS only): html2canvas, in case html-to-image kept producing
    // blank image regions even after the verified retries.
    if (isIOSWebKit() && typeof window.html2canvas === 'function') {
      const objectFitOriginals = preProcessObjectFitImages(element);
      const textOriginals = preProcessTextForIOSCapture(element);
      let h2cBlob: Blob | null = null;
      try {
        element.offsetHeight; // force reflow
        const canvas = await window.html2canvas(element, {
          width,
          height,
          backgroundColor: bgColor,
          scale: 1,
          useCORS: true,
          allowTaint: false,
          logging: false,
        });
        h2cBlob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob((b) => resolve(b), 'image/png');
        });
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = 0;
        canvas.height = 0;
      } catch {
        h2cBlob = null;
      } finally {
        restoreTextAfterIOSCapture(textOriginals);
        restoreObjectFitImages(objectFitOriginals);
      }
      if (h2cBlob && !(await hasBlankImageRegions(h2cBlob, element))) return h2cBlob;
    }

    return blob;
  } finally {
    restoreOriginalImages(inlined);
  }
}

export interface ExportProgress {
  done: number;
  total: number;
}

export interface ZipExportOptions {
  width: number;
  height: number;
  bgColor: string;
  onProgress?: (progress: ExportProgress) => void;
}

/**
 * Captures each element to PNG and packs them into a ZIP blob. Pre-fetches
 * external image URLs once so per-slide captures can inline them with no
 * extra network round-trip.
 */
export async function zipSlideElements(
  elements: HTMLElement[],
  options: ZipExportOptions,
): Promise<Blob> {
  const urls = new Set<string>();
  elements.forEach((el) => {
    el.querySelectorAll('img').forEach((img) => {
      const src = img.src;
      if (src && !src.startsWith('data:') && !src.startsWith('blob:')) {
        urls.add(src);
      }
    });
  });
  const imageCache = await prefetchAllImageUrls(urls);

  // Embed the web fonts actually used by the slides so the exported PNG matches
  // the live preview (otherwise serif/display fonts fall back to system fonts).
  try { await (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts?.ready; } catch { /* noop */ }
  const fontEmbedCSS = await getFontEmbedCSS(collectUsedFontFaces(elements));

  const zip = new window.JSZip();
  for (let i = 0; i < elements.length; i++) {
    const blob = await captureElementAsBlob(elements[i], {
      width: options.width,
      height: options.height,
      bgColor: options.bgColor,
      imageCache,
      fontEmbedCSS,
    });
    if (blob) zip.file(`slide-${i + 1}.png`, blob);
    options.onProgress?.({ done: i + 1, total: elements.length });
  }
  return zip.generateAsync({ type: 'blob' });
}

/**
 * Triggers a browser download for a blob via the FileSaver CDN lib.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  window.saveAs(blob, filename);
}
